import React, { useMemo } from 'react';
import { Col, Grid, Row, Text } from 'components';
import { FontAwesome5 } from '@expo/vector-icons';
import { StyleSheet, ScrollView } from 'react-native';
import KPICard from './KPICard';
import DailyVolumeChart from './DailyVolumeChart';
import ProductCategoryChart from './ProductCategoryChart';
import DriverLeaderboard from './DriverLeaderboard';
import { TDashboardSnapshot } from 'store/api-client';
import moment from 'moment';
import ValHelper from 'helpers/ValHelper';
import IssuesCountCard from './IssuesCountCard';
import OldestOpenIssueCard from './OldestOpenIssueCard';
import OrdersAtRiskCard from './OrdersAtRiskCard';

const MSC_BLUE = '#004B8D';
const SUCCESS = '#1FA463';
const WARNING = '#F5A623';
const STEEL_GREY = '#E0E2E5';

interface KPIPanelProps {
  snapshots: Array<TDashboardSnapshot>;
  issuesByDriver?: Array<{
    driverReference: string;
    driverName: string;
    totalOrders: number;
    ordersWithIssues: number;
    issueFreeRate: number;
  }>;
  movingVehiclesCount?: number;
  totalStopsRemaining?: number;
}

const KPIPanel: React.FC<KPIPanelProps> = ({ snapshots, issuesByDriver = [], movingVehiclesCount = 0, totalStopsRemaining = 0 }) => {
  // Calculate KPIs from snapshots
  const kpis = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
    
    const todaySnapshot = snapshots.find(s => s.snapshotDate === today);
    const yesterdaySnapshot = snapshots.find(s => s.snapshotDate === yesterday);
    
    // On-time delivery rate: onTimeOrderReferenceNumbers.length / delivered
    const todayOnTimeOrders = ValHelper.parseArrayField(todaySnapshot?.onTimeOrderReferenceNumbers).length;
    const todayDeliveryRate = todaySnapshot && todaySnapshot.delivered > 0
      ? (todayOnTimeOrders / todaySnapshot.delivered) * 100
      : 0;
    
    const yesterdayOnTimeOrders = ValHelper.parseArrayField(yesterdaySnapshot?.onTimeOrderReferenceNumbers).length;
    const yesterdayDeliveryRate = yesterdaySnapshot && yesterdaySnapshot.delivered > 0
      ? (yesterdayOnTimeOrders / yesterdaySnapshot.delivered) * 100
      : 0;
    
    // Orders without issues: (totalOrders - totalOpenIssues) / totalOrders
    const todayOrdersWithoutIssues = todaySnapshot && todaySnapshot.totalOrders > 0
      ? ((todaySnapshot.totalOrders - todaySnapshot.totalOpenIssues) / todaySnapshot.totalOrders) * 100
      : 0;
    
    // Average delivery duration from snapshot
    const avgDeliveryDuration = todaySnapshot?.avgDeliveryDurationMinutes || 0;
    const yesterdayAvgDeliveryDuration = yesterdaySnapshot?.avgDeliveryDurationMinutes || 0;
    
    // Average issue resolution time from snapshot
    const avgIssueResolutionMinutes = todaySnapshot?.avgIssueResolutionMinutes || 0;
    const yesterdayAvgIssueResolutionMinutes = yesterdaySnapshot?.avgIssueResolutionMinutes || 0;
    
    // Format issue resolution time
    const formatResolutionTime = (minutes: number): string => {
      if (minutes === 0) return 'N/A';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      }
      return `${mins}m`;
    };
    
    const avgIssueResolution = formatResolutionTime(avgIssueResolutionMinutes);
    
    return {
      onTimeDeliveryRate: {
        value: todayDeliveryRate.toFixed(1) + '%',
        progress: todayDeliveryRate,
        trend: yesterdaySnapshot ? {
          direction: todayDeliveryRate >= yesterdayDeliveryRate ? 'up' as const : 'down' as const,
          text: `${Math.abs(todayDeliveryRate - yesterdayDeliveryRate).toFixed(1)}% ${todayDeliveryRate >= yesterdayDeliveryRate ? 'improvement' : 'decrease'} from yesterday`,
        } : undefined,
      },
      avgDeliveryDuration: {
        value: avgDeliveryDuration > 0 ? avgDeliveryDuration.toString() : 'N/A',
        trend: yesterdaySnapshot && avgDeliveryDuration > 0 && yesterdayAvgDeliveryDuration > 0 ? {
          direction: avgDeliveryDuration <= yesterdayAvgDeliveryDuration ? 'down' as const : 'up' as const,
          text: (() => {
            const percentChange = ((yesterdayAvgDeliveryDuration - avgDeliveryDuration) / yesterdayAvgDeliveryDuration) * 100;
            const absPercentChange = Math.abs(percentChange);
            if (avgDeliveryDuration <= yesterdayAvgDeliveryDuration) {
              return `${absPercentChange.toFixed(1)}% faster than yesterday`;
            } else {
              return `${absPercentChange.toFixed(1)}% slower than yesterday`;
            }
          })(),
        } : undefined,
      },
      ordersWithoutIssues: {
        value: todayOrdersWithoutIssues.toFixed(1) + '%',
        progress: todayOrdersWithoutIssues,
      },
      avgIssueResolution: {
        value: avgIssueResolution,
        trend: yesterdaySnapshot && avgIssueResolutionMinutes > 0 && yesterdayAvgIssueResolutionMinutes > 0 ? {
          direction: avgIssueResolutionMinutes <= yesterdayAvgIssueResolutionMinutes ? 'down' as const : 'up' as const,
          text: formatResolutionTime(Math.abs(avgIssueResolutionMinutes - yesterdayAvgIssueResolutionMinutes)) + ` ${avgIssueResolutionMinutes <= yesterdayAvgIssueResolutionMinutes ? 'improvement' : 'increase'}`,
        } : undefined,
      },
      issuesByType: todaySnapshot?.issuesByType || {},
    };
  }, [snapshots]);
  
  // Get last 7 days of snapshots for DailyVolumeChart
  const last7DaysSnapshots = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
      const snapshot = snapshots.find(s => s.snapshotDate === date);
      days.push({
        date,
        dayName: moment(date).format('ddd'),
        ordersDeliveredToday: snapshot?.ordersDeliveredToday || 0,
      });
    }
    return days;
  }, [snapshots]);
  return (
    <Col style={styles.centerPanel}>
      <Row alignItems="center" marginBottom={24}>
        <Col style={styles.sectionIcon}>
          <FontAwesome5 name="chart-line" size={16} color="white" />
        </Col>
        <Text fontSize={20} bold style={{ color: MSC_BLUE, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          KEY PERFORMANCE METRICS
        </Text>
      </Row>

      <ScrollView showsVerticalScrollIndicator={false}>
        <Col>
          {/* KPI Grid */}
          <Grid xs='50%' margin={-8} marginBottom={12} alignItems="stretch">
            <Col flex1 padding={8}>
              <KPICard
                value={kpis.onTimeDeliveryRate.value}
                label="ON-TIME DELIVERY RATE"
                color={SUCCESS}
                progress={kpis.onTimeDeliveryRate.progress}
                trend={kpis.onTimeDeliveryRate.trend}
              />
            </Col>
            <Col flex1 padding={8}>
              <KPICard
                value={kpis.avgDeliveryDuration.value}
                label="AVG DELIVERY DURATION (MINS)"
                color={MSC_BLUE}
                trend={kpis.avgDeliveryDuration.trend}
              />
            </Col>
            <Col flex1 padding={8}>
              <KPICard
                value={kpis.ordersWithoutIssues.value}
                label="ORDERS WITHOUT ISSUES"
                color={SUCCESS}
                progress={kpis.ordersWithoutIssues.progress}
              />
            </Col>
            <Col flex1 padding={8}>
              <KPICard
                value={kpis.avgIssueResolution.value}
                label="AVG ISSUE RESOLUTION"
                color={WARNING}
                trend={kpis.avgIssueResolution.trend}
              />
            </Col>
            <Col flex1 padding={8}>
              <IssuesCountCard
                issuesByType={kpis.issuesByType || {}}
              />
            </Col>
            <Col flex1 padding={8}>
              <OldestOpenIssueCard />
            </Col>
            <Col flex1 padding={8}>
              <OrdersAtRiskCard useMockData={true} />
            </Col>
            <Col flex1 padding={8}>
              <KPICard
                value={movingVehiclesCount.toString()}
                label="MOVING VEHICLES"
                color={MSC_BLUE}
              />
            </Col>
            <Col flex1 padding={8}>
              <KPICard
                value={totalStopsRemaining.toString()}
                label="STOPS REMAINING"
                color={WARNING}
              />
            </Col>
          </Grid>
          <DriverLeaderboard drivers={issuesByDriver} />
          <DailyVolumeChart snapshots={last7DaysSnapshots} />
          {/* <ProductCategoryChart /> */}
        </Col>
      </ScrollView>
    </Col>
  );
};

const styles = StyleSheet.create({
  centerPanel: {
    flex: 1,
  },
  sectionIcon: {
    width: 32,
    height: 32,
    backgroundColor: MSC_BLUE,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
});

export default KPIPanel;

